import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  createInstitutionalAct,
  uploadInstitutionalActDocument,
  updateInstitutionalAct,
  type InstitutionalActInput,
  type InstitutionalActRecord,
  type ReviewOrganization,
} from '@urbanik/api-client';
import { Button, CanonicalModal, TextField, TextAreaField, SelectField } from '@urbanik/ui';
import { reviewApiBase } from './api-base';
export function InstitutionalActEditor({
  organization,
  act,
  onClose,
  onSaved,
}: {
  organization: ReviewOrganization;
  act: InstitutionalActRecord | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<InstitutionalActInput>(
    act
      ? {
          organ: act.organ,
          date: act.date,
          sessionType: act.sessionType,
          subject: act.subject,
          resolutions: act.resolutions,
          effectiveFrom: act.effectiveFrom,
          effectiveTo: act.effectiveTo,
        }
      : {
          organ: 'general_assembly',
          date: '',
          sessionType: 'ordinary',
          subject: '',
          resolutions: '',
          effectiveFrom: '',
          effectiveTo: null,
        },
  );
  const [saving, setSaving] = useState(false),
    [error, setError] = useState(''),
    [file, setFile] = useState<File | null>(null),
    [reason, setReason] = useState(''),
    [createdAct, setCreatedAct] = useState<InstitutionalActRecord | null>(null);
  const pending = useRef<AbortController | null>(null);
  useEffect(() => () => pending.current?.abort(), []);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    const controller = new AbortController();
    pending.current = controller;
    setSaving(true);
    setError('');
    try {
      if (act) {
        const editable = {
          sessionType: draft.sessionType,
          subject: draft.subject,
          resolutions: draft.resolutions,
          effectiveFrom: draft.effectiveFrom,
          effectiveTo: draft.effectiveTo,
        };
        await updateInstitutionalAct(
          reviewApiBase,
          organization.id,
          act.id,
          { ...editable, expectedVersion: act.version },
          controller.signal,
        );
      } else {
        if (!file || reason.trim().length < 5 || reason.trim().length > 500) {
          setError('Selecciona el PDF del acta e indica un motivo de 5 a 500 caracteres.');
          return;
        }
        const created =
          createdAct ??
          (await createInstitutionalAct(reviewApiBase, organization.id, draft, controller.signal));
        if (!createdAct && !controller.signal.aborted) setCreatedAct(created);
        await uploadInstitutionalActDocument(
          reviewApiBase,
          organization.id,
          created.id,
          file,
          created.version,
          reason.trim(),
          controller.signal,
        );
      }
      if (!controller.signal.aborted) onSaved();
    } catch (e) {
      if (!controller.signal.aborted)
        setError(e instanceof Error ? e.message : 'No se pudo guardar.');
    } finally {
      if (!controller.signal.aborted) setSaving(false);
    }
  }
  return (
    <CanonicalModal
      open
      title={act ? 'Editar acta de ensayo' : 'Nueva acta de ensayo'}
      description={'Entidad: ' + organization.name + ' · ' + organization.code}
      onClose={() => {
        if (!saving) onClose();
      }}
    >
      <form onSubmit={(event) => void save(event)} className="concepts-form">
        {error ? <p role="alert">{error}</p> : null}
        <TextField
          label="Número de acta"
          value={act?.number ?? 'Se generará por entidad, órgano y año'}
          disabled
        />
        <SelectField
          label="Órgano emisor"
          value={draft.organ}
          disabled={saving || !!act}
          options={[
            { value: 'general_assembly', label: 'Asamblea general' },
            { value: 'administration_council', label: 'Consejo de administración' },
            { value: 'surveillance_council', label: 'Consejo de vigilancia' },
          ]}
          onValueChange={(organ) =>
            setDraft({ ...draft, organ: organ as InstitutionalActInput['organ'] })
          }
        />
        <TextField
          label="Fecha del acta"
          type="date"
          required
          value={draft.date}
          disabled={saving || !!act}
          onChange={(e) => setDraft({ ...draft, date: e.target.value })}
        />
        <SelectField
          label="Tipo de sesión"
          value={draft.sessionType}
          disabled={saving}
          options={[
            { value: 'ordinary', label: 'Ordinaria' },
            { value: 'extraordinary', label: 'Extraordinaria' },
            { value: 'informative', label: 'Informativa' },
          ]}
          onValueChange={(sessionType) =>
            setDraft({ ...draft, sessionType: sessionType as InstitutionalActInput['sessionType'] })
          }
        />
        <TextField
          label="Asunto"
          required
          minLength={3}
          maxLength={300}
          value={draft.subject}
          disabled={saving}
          onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
        />
        <TextAreaField
          label="Resoluciones"
          required
          minLength={3}
          maxLength={10000}
          value={draft.resolutions}
          disabled={saving}
          onChange={(e) => setDraft({ ...draft, resolutions: e.target.value })}
        />
        <TextField
          label="Inicio de vigencia"
          type="date"
          required
          value={draft.effectiveFrom}
          disabled={saving}
          onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })}
        />
        <TextField
          label="Fin de vigencia"
          type="date"
          value={draft.effectiveTo ?? ''}
          disabled={saving}
          onChange={(e) => setDraft({ ...draft, effectiveTo: e.target.value || null })}
        />
        {!act ? (
          <>
            <label className="ui-field">
              PDF del acta (máximo 20 MB) *
              <input
                type="file"
                accept=".pdf,application/pdf"
                required
                disabled={saving || !!createdAct}
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
            </label>
            <TextAreaField
              label="Motivo de carga"
              required
              minLength={5}
              maxLength={500}
              value={reason}
              disabled={saving}
              onChange={(event) => setReason(event.target.value)}
            />
            <p>
              El PDF y el motivo son obligatorios. El documento quedará aprobado y registrado en el
              historial junto con el acta.
            </p>
          </>
        ) : (
          <p>Editar los datos no reemplaza el PDF ni genera obligaciones o pagos.</p>
        )}
        <Button type="button" disabled={saving} onClick={onClose}>
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={
            saving || (!act && (!file || reason.trim().length < 5 || reason.trim().length > 500))
          }
        >
          {saving ? 'Guardando…' : 'Guardar acta'}
        </Button>
      </form>
    </CanonicalModal>
  );
}
